import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import RecruiterSidebar from '../components/RecruiterSidebar';
import { API, MEDIA_URL } from '../config';
import '../styles/theme.css';
import './JobApplications.css';

const STATUS_MAP = {
  pending:   { cls: 'badge-orange', icon: 'fa-clock',          text: 'Pending' },
  applied:   { cls: 'badge-blue',   icon: 'fa-paper-plane',    text: 'Applied' },
  viewed:    { cls: 'badge-gray',   icon: 'fa-eye',            text: 'Viewed' },
  interview: { cls: 'badge-green',  icon: 'fa-calendar-check', text: 'Interview' },
  rejected:  { cls: 'badge-red',    icon: 'fa-times-circle',   text: 'Rejected' },
  accepted:  { cls: 'badge-green',  icon: 'fa-check-circle',   text: 'Accepted' },
  hired:     { cls: 'badge-purple', icon: 'fa-user-tie',       text: 'Hired' },
};

const INTERVIEW_RESPONSE_MAP = {
  pending:  { cls: 'badge-orange', icon: 'fa-hourglass-half', text: 'Awaiting Response' },
  accepted: { cls: 'badge-green',  icon: 'fa-check',          text: 'Confirmed' },
  declined: { cls: 'badge-red',    icon: 'fa-times',          text: 'Declined by Seeker' },
};

function StatusBadge({ status }) {
  const c = STATUS_MAP[status] || STATUS_MAP.pending;
  return <span className={`badge ${c.cls}`}><i className={`fas ${c.icon}`}></i> {c.text}</span>;
}

function StatusTimeline({ status, interviewResponse }) {
  const steps = [
    { key: 'applied',   label: 'Applied',   icon: 'fa-paper-plane' },
    { key: 'interview', label: 'Interview',  icon: 'fa-calendar-check' },
    { key: 'hired',     label: 'Hired',      icon: 'fa-user-tie' },
  ];
  const order = ['pending','applied','viewed','interview','accepted','hired'];
  const currentIdx = order.indexOf(status);
  return (
    <div className="ja-timeline">
      {steps.map((step, i) => {
        const stepIdx = order.indexOf(step.key);
        const done = currentIdx >= stepIdx;
        const active = status === step.key || (step.key === 'interview' && status === 'accepted');
        return (
          <React.Fragment key={step.key}>
            <div className={`ja-timeline-step ${done ? 'done' : ''} ${active ? 'active' : ''}`}>
              <div className="ja-timeline-dot"><i className={`fas ${step.icon}`}></i></div>
              <span className="ja-timeline-label">{step.label}</span>
              {step.key === 'interview' && interviewResponse && done && (
                <span className={`badge ${INTERVIEW_RESPONSE_MAP[interviewResponse]?.cls || 'badge-gray'} ja-timeline-sub`}>
                  {INTERVIEW_RESPONSE_MAP[interviewResponse]?.text}
                </span>
              )}
            </div>
            {i < steps.length - 1 && <div className={`ja-timeline-line ${currentIdx > stepIdx ? 'done' : ''}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function JobApplications() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [applications, setApplications] = useState([]);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Email modal
  const [emailModal, setEmailModal] = useState({ show: false, app: null });
  const [emailData, setEmailData] = useState({ subject: '', body: '' });
  const [rejectNotifyModal, setRejectNotifyModal] = useState({ show: false, app: null });
  const [rejectNotifyReason, setRejectNotifyReason] = useState('');
  const [rejectNotifyTemplateId, setRejectNotifyTemplateId] = useState('');
  const [emailTemplates, setEmailTemplates] = useState([]);
  // Bulk actions
  const [selected, setSelected] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('reject');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const tier = localStorage.getItem('user_tier') || 'starter';
  const isBusiness = tier === 'business';

  useEffect(() => { fetchJobAndApplications(); }, [jobId]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API}/email-templates/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(d => setEmailTemplates(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const openEmailModal = (app) => {
    setEmailData({
      subject: `Regarding your application for ${job?.title} at ${job?.company}`,
      body: `Dear ${app.applicant_name},\n\nThank you for applying for the ${job?.title} position at ${job?.company}.\n\n`,
    });
    setEmailModal({ show: true, app });
  };

  const applyTemplate = (templateId, target = 'email') => {
    const t = emailTemplates.find(t => t.id === parseInt(templateId));
    if (!t) return;
    const app = target === 'email' ? emailModal.app : applications.find(a => a.id === inviteModal.applicationId);
    const merge = (str) => str
      .replace(/\{\{applicant_name\}\}/g, app?.applicant_name || '')
      .replace(/\{\{job_title\}\}/g, job?.title || '')
      .replace(/\{\{company_name\}\}/g, job?.company || '')
      .replace(/\{\{interview_date\}\}/g, inviteData.date || '')
      .replace(/\{\{interview_time\}\}/g, inviteData.time || '')
      .replace(/\{\{interview_location\}\}/g, inviteData.location || '');
    if (target === 'email') {
      setEmailData({ subject: merge(t.subject), body: merge(t.body) });
    }
  };

  const handleSendEmail = () => {
    const { app } = emailModal;
    const mailto = `mailto:${app.applicant_email}?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(emailData.body)}`;
    window.open(mailto, '_blank');
    setEmailModal({ show: false, app: null });
  };

  // Decline modal
  const [declineModal, setDeclineModal] = useState({ show: false, applicationId: null });
  const [declineReason, setDeclineReason] = useState('');

  // Interview invite modal
  const [inviteModal, setInviteModal] = useState({ show: false, applicationId: null });
  const [inviteData, setInviteData] = useState({ date: '', time: '', location: '', notes: '' });

  const fetchJobAndApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const [jobRes, appsRes] = await Promise.all([
        fetch(`${API}/jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/applications/job/${jobId}/applications/`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setJob(await jobRes.json());
      const appsData = await appsRes.json();
      const normalized = Array.isArray(appsData)
        ? appsData.map(a => ({ ...a, status: a.status?.toLowerCase(), interview_response: a.interview_response?.toLowerCase() }))
        : [];

      // Fetch profile photos for each applicant
      const enriched = await Promise.all(normalized.map(async (app) => {
        try {
          const profileRes = await fetch(`${API}/job-seeker-profile/user/${app.user_id}/`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (profileRes.ok) {
            const profile = await profileRes.json();
            return { ...app, profile_photo: profile.profile_photo || null };
          }
        } catch {}
        return app;
      }));

      setApplications(enriched);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/applications/${declineModal.applicationId}/`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'rejected', rejection_reason: declineReason }),
      });
      if (!res.ok) throw new Error();
      await fetchJobAndApplications();
      setDeclineModal({ show: false, applicationId: null });
      setDeclineReason('');
      if (selectedApplication?.id === declineModal.applicationId) setSelectedApplication(null);
    } catch { alert('Failed to decline application.'); }
    finally { setProcessing(false); }
  };

  const handleSendInvite = async () => {
    if (!inviteData.date || !inviteData.time || !inviteData.location.trim()) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/applications/${inviteModal.applicationId}/invite-interview/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interview_date: inviteData.date,
          interview_time: inviteData.time,
          interview_location: inviteData.location,
          interview_notes: inviteData.notes || undefined,
        }),
      });
      if (!res.ok) throw new Error();
      await fetchJobAndApplications();
      setInviteModal({ show: false, applicationId: null });
      setInviteData({ date: '', time: '', location: '', notes: '' });
      if (selectedApplication?.id === inviteModal.applicationId) setSelectedApplication(null);
    } catch { alert('Failed to send interview invitation.'); }
    finally { setProcessing(false); }
  };

  const handleBulkAction = async () => {
    if (selected.size === 0) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/applications/bulk-action/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_ids: Array.from(selected),
          action: bulkAction,
          rejection_reason: bulkReason || undefined,
          template_id: bulkTemplateId ? parseInt(bulkTemplateId) : null,
        }),
      });
      if (!res.ok) { const d = await res.json(); alert(d.detail || 'Failed'); return; }
      await fetchJobAndApplications();
      setSelected(new Set());
      setBulkModal(false);
      setBulkReason('');
    } catch { alert('Bulk action failed.'); }
    finally { setProcessing(false); }
  };

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const selectAll = () => setSelected(new Set(applications.map(a => a.id)));
  const clearAll = () => setSelected(new Set());

  const handleRejectAndNotify = async () => {
    const { app } = rejectNotifyModal;
    if (!app) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/applications/${app.id}/reject-and-notify/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rejection_reason: rejectNotifyReason || undefined,
          template_id: rejectNotifyTemplateId ? parseInt(rejectNotifyTemplateId) : null,
        }),
      });
      if (!res.ok) throw new Error();
      await fetchJobAndApplications();
      setRejectNotifyModal({ show: false, app: null });
      setRejectNotifyReason('');
      setRejectNotifyTemplateId('');
    } catch { alert('Failed to reject applicant.'); }
    finally { setProcessing(false); }
  };

  const handleHire = async (applicationId) => {
    if (!window.confirm('Mark this candidate as Hired?')) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/applications/${applicationId}/hire/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      await fetchJobAndApplications();
      if (selectedApplication?.id === applicationId) setSelectedApplication(null);
    } catch { alert('Failed to hire candidate.'); }
    finally { setProcessing(false); }
  };

  if (loading) return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-content">
          <div className="empty-state"><i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i><p>Loading applications...</p></div>
        </div>
      </main>
    </div>
  );

  return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/recruiter/dashboard')}>
              <i className="fas fa-arrow-left"></i> Dashboard
            </button>
            <span className="topbar-title">Applications</span>
          </div>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme} title="Toggle theme"><i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i></button>
            <span className="badge badge-blue">
              <i className="fas fa-users"></i> {applications.length} Applicant{applications.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="portal-content">
          <div className="card ja-job-header">
            <div className="ja-job-title-wrap">
              <div className="ja-job-icon"><i className="fas fa-briefcase"></i></div>
              <div>
                <h1 className="ja-job-title">{job?.title}</h1>
                <p className="ja-job-meta">
                  <i className="fas fa-building"></i> {job?.company}
                  {job?.city && <><span className="ja-dot">•</span><i className="fas fa-map-marker-alt"></i> {job.city}</>}
                  {job?.employment_type && <><span className="ja-dot">•</span>{job.employment_type}</>}
                </p>
              </div>
            </div>
            <div className="ja-job-stats">
              <div className="ja-stat"><span className="ja-stat-num">{applications.length}</span><span className="ja-stat-lbl">Total</span></div>
              <div className="ja-stat"><span className="ja-stat-num">{applications.filter(a => a.status === 'interview').length}</span><span className="ja-stat-lbl">Interviews</span></div>
              <div className="ja-stat"><span className="ja-stat-num">{applications.filter(a => a.status === 'hired').length}</span><span className="ja-stat-lbl">Hired</span></div>
              <div className="ja-stat"><span className="ja-stat-num">{applications.filter(a => a.status === 'rejected').length}</span><span className="ja-stat-lbl">Declined</span></div>
            </div>
          </div>

          {applications.length === 0 ? (
            <div className="empty-state" style={{ marginTop: '1.5rem' }}>
              <i className="fas fa-inbox"></i>
              <h3>No Applications Yet</h3>
              <p>Applications will appear here once job seekers apply to this position.</p>
            </div>
          ) : (
            <>
              {/* Bulk actions toolbar */}
              {isBusiness && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary btn-sm" onClick={selected.size === applications.length ? clearAll : selectAll}>
                    <i className={`fas ${selected.size === applications.length ? 'fa-times-square' : 'fa-check-square'}`}></i>
                    {selected.size === applications.length ? 'Deselect All' : 'Select All'}
                  </button>
                  {selected.size > 0 && (
                    <>
                      <span className="badge badge-blue">{selected.size} selected</span>
                      <button className="btn btn-primary btn-sm" onClick={() => setBulkModal(true)}>
                        <i className="fas fa-bolt"></i> Bulk Action
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={clearAll}>Clear</button>
                    </>
                  )}
                  {!isBusiness && (
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      <i className="fas fa-lock" style={{ marginRight: 4 }}></i>Bulk actions require Business plan
                    </span>
                  )}
                </div>
              )}
            <div className="ja-grid">
              {applications.map((app, idx) => {
                const score = app.match_score || 0;
                const scoreColor = score >= 75 ? '#640ff7' : score >= 65 ? '#34f76b' : score >= 50 ? '#eef734' : '#f73444';
                const R = 20, CIRC = 2 * Math.PI * R;
                const dash = (score / 100) * CIRC;
                const canInvite = !['interview','rejected','hired'].includes(app.status);
                const canHire = app.status === 'interview' && app.interview_response === 'accepted';
                return (
                  <div key={app.id} className={`card ja-card${selected.has(app.id) ? ' ja-card-selected' : ''}`}
                    style={selected.has(app.id) ? { borderColor: 'var(--primary)' } : {}}>
                    {isBusiness && (
                      <div style={{ position: 'absolute', top: 10, left: 10 }}>
                        <input type="checkbox" checked={selected.has(app.id)} onChange={() => toggleSelect(app.id)}
                          style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--primary)' }} />
                      </div>
                    )}
                    <div className="ja-card-top">
                      <div className="ja-applicant">
                        <div className="ja-score-wrap" title={`Match score: ${score}%`}>
                          <svg width="50" height="50" viewBox="0 0 50 50">
                            <circle cx="25" cy="25" r={R} fill="none" stroke="var(--border)" strokeWidth="3.5" />
                            <circle cx="25" cy="25" r={R} fill="none" stroke={scoreColor} strokeWidth="3.5"
                              strokeLinecap="round" strokeDasharray={`${dash} ${CIRC}`} transform="rotate(-90 25 25)" />
                            <circle cx="25" cy="25" r="15" fill="var(--surface-2)" />
                            <text x="25" y="29" textAnchor="middle" fontSize="10" fontWeight="700" fill={scoreColor}>{Math.round(score)}%</text>
                          </svg>
                          {idx === 0 && <span className="ja-top-badge">Top</span>}
                        </div>
                        <div>
                          <div className="ja-name">{app.applicant_name}</div>
                          <div className="ja-email">{app.applicant_email}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem' }}>
                        <StatusBadge status={app.status} />
                        {app.is_below_threshold && app.status !== 'rejected' && (
                          <span className="badge badge-red" style={{ fontSize: '0.7rem' }}>
                            <i className="fas fa-exclamation-triangle" style={{ marginRight: 3 }}></i>Below Threshold
                          </span>
                        )}
                      </div>
                    </div>

                    {app.status === 'interview' && app.interview_response && (
                      <div className="ja-interview-response-row">
                        <span className={`badge ${INTERVIEW_RESPONSE_MAP[app.interview_response]?.cls || 'badge-gray'}`}>
                          <i className={`fas ${INTERVIEW_RESPONSE_MAP[app.interview_response]?.icon}`}></i>{' '}
                          {INTERVIEW_RESPONSE_MAP[app.interview_response]?.text}
                        </span>
                        {app.interview_date && (
                          <span className="ja-interview-meta">
                            <i className="fas fa-calendar-alt"></i> {app.interview_date} at {app.interview_time}
                          </span>
                        )}
                      </div>
                    )}

                    {app.matched_skills?.length > 0 && (
                      <div className="ja-matched-skills">
                        {app.matched_skills.slice(0, 4).map((s, i) => <span key={i} className="badge badge-blue">{s}</span>)}
                        {app.matched_skills.length > 4 && <span className="badge badge-gray">+{app.matched_skills.length - 4}</span>}
                      </div>
                    )}

                    <div className="ja-card-meta">
                      {app.applicant_phone && <span><i className="fas fa-phone"></i> {app.applicant_phone}</span>}
                      <span><i className="fas fa-calendar"></i> {new Date(app.applied_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>

                    {(app.cv_file_path || app.portfolio_file_path || app.cover_letter_file_path) && (
                      <div className="ja-docs">
                        {app.cv_file_path && <span className="ja-doc-badge"><i className="fas fa-file-pdf"></i> CV</span>}
                        {app.portfolio_file_path && <span className="ja-doc-badge"><i className="fas fa-folder"></i> Portfolio</span>}
                        {app.cover_letter_file_path && <span className="ja-doc-badge"><i className="fas fa-file-alt"></i> Cover Letter</span>}
                      </div>
                    )}

                    <div className="ja-card-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => setSelectedApplication(app)}>
                        <i className="fas fa-eye"></i> View
                      </button>
                      <button className="btn btn-sm ja-btn-email" onClick={() => openEmailModal(app)}>
                        <i className="fas fa-envelope"></i> Email
                      </button>
                      {app.is_below_threshold && app.status !== 'rejected' && (
                        <button className="btn btn-danger btn-sm" onClick={() => { setRejectNotifyModal({ show: true, app }); setRejectNotifyReason(''); setRejectNotifyTemplateId(''); }}
                          title="Reject and send notification email">
                          <i className="fas fa-times-circle"></i> Reject & Notify
                        </button>
                      )}
                      {canInvite && !app.is_below_threshold && (
                        <>
                          <button className="btn btn-sm ja-btn-interview" onClick={() => setInviteModal({ show: true, applicationId: app.id })}>
                            <i className="fas fa-calendar-check"></i> Invite
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => { setDeclineModal({ show: true, applicationId: app.id }); setDeclineReason(''); }}>
                            <i className="fas fa-times"></i> Decline
                          </button>
                        </>
                      )}
                      {canHire && (
                        <button className="btn btn-sm ja-btn-hire" onClick={() => handleHire(app.id)} disabled={processing}>
                          <i className="fas fa-user-tie"></i> Hire
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      </main>

      {/* Application Details Modal */}
      {selectedApplication && (
        <div className="modal-overlay" onClick={() => setSelectedApplication(null)}>
          <div className="modal ja-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div
                  className="ja-avatar"
                  onClick={() => { setSelectedApplication(null); navigate(`/recruiter/seeker-profile/${selectedApplication.user_id}`); }}
                  title="View full profile"
                  style={{ cursor: 'pointer', overflow: 'hidden', flexShrink: 0 }}
                >
                  {selectedApplication.profile_photo
                    ? <img
                        src={`${MEDIA_URL}${selectedApplication.profile_photo.startsWith('/') ? '' : '/'}${selectedApplication.profile_photo}`}
                        alt={selectedApplication.applicant_name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                      />
                    : selectedApplication.applicant_name?.charAt(0).toUpperCase()
                  }
                </div>
                <div>
                  <h2
                    style={{ padding: 0, border: 'none', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, cursor: 'pointer' }}
                    onClick={() => { setSelectedApplication(null); navigate(`/recruiter/seeker-profile/${selectedApplication.user_id}`); }}
                    title="View full profile"
                  >
                    {selectedApplication.applicant_name}
                    <i className="fas fa-external-link-alt" style={{ fontSize: '0.7rem', marginLeft: '0.4rem', color: 'var(--text-muted)' }}></i>
                  </h2>
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0 }}>{selectedApplication.applicant_email}</p>
                </div>
              </div>
              <button className="modal-close" onClick={() => setSelectedApplication(null)}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              <div className="ja-detail-status-row">
                <StatusBadge status={selectedApplication.status} />
                <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                  <i className="fas fa-calendar" style={{ marginRight: '0.35rem' }}></i>
                  Applied {new Date(selectedApplication.applied_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                {selectedApplication.applicant_phone && (
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                    <i className="fas fa-phone" style={{ marginRight: '0.35rem' }}></i>{selectedApplication.applicant_phone}
                  </span>
                )}
              </div>

              <StatusTimeline status={selectedApplication.status} interviewResponse={selectedApplication.interview_response} />

              {/* Interview details */}
              {selectedApplication.status === 'interview' && selectedApplication.interview_date && (
                <div className="ja-detail-section ja-section-success">
                  <div className="ja-detail-section-title"><i className="fas fa-calendar-check"></i> Interview Invitation</div>
                  <div className="ja-interview-details">
                    <span><i className="fas fa-calendar-alt"></i> {selectedApplication.interview_date}</span>
                    <span><i className="fas fa-clock"></i> {selectedApplication.interview_time}</span>
                    <span><i className="fas fa-map-marker-alt"></i> {selectedApplication.interview_location}</span>
                  </div>
                  {selectedApplication.interview_response && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <span className={`badge ${INTERVIEW_RESPONSE_MAP[selectedApplication.interview_response]?.cls || 'badge-gray'}`}>
                        <i className={`fas ${INTERVIEW_RESPONSE_MAP[selectedApplication.interview_response]?.icon}`}></i>{' '}
                        Seeker: {INTERVIEW_RESPONSE_MAP[selectedApplication.interview_response]?.text}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Documents */}
              <div className="ja-detail-section">
                <div className="ja-detail-section-title"><i className="fas fa-file-alt"></i> Documents</div>
                {!selectedApplication.cv_file_path && !selectedApplication.portfolio_file_path && !selectedApplication.cover_letter_file_path ? (
                  <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.875rem' }}>No documents submitted</p>
                ) : (
                  <div className="ja-documents-list">
                    {selectedApplication.cv_file_path && (
                      <div className="ja-document-item">
                        <i className="fas fa-file-pdf" style={{ color: 'var(--danger)' }}></i>
                        <span>CV / Resume</span>
                        <button className="btn btn-primary btn-sm" onClick={() => window.open(`${MEDIA_URL}/${selectedApplication.cv_file_path}`, '_blank')}><i className="fas fa-download"></i> Download</button>
                      </div>
                    )}
                    {selectedApplication.portfolio_file_path && (
                      <div className="ja-document-item">
                        <i className="fas fa-folder" style={{ color: 'var(--warning)' }}></i>
                        <span>Portfolio</span>
                        <button className="btn btn-primary btn-sm" onClick={() => window.open(`${MEDIA_URL}/${selectedApplication.portfolio_file_path}`, '_blank')}><i className="fas fa-download"></i> Download</button>
                      </div>
                    )}
                    {selectedApplication.cover_letter_file_path && (
                      <div className="ja-document-item">
                        <i className="fas fa-file-alt" style={{ color: 'var(--primary)' }}></i>
                        <span>Cover Letter</span>
                        <button className="btn btn-primary btn-sm" onClick={() => window.open(`${MEDIA_URL}/${selectedApplication.cover_letter_file_path}`, '_blank')}><i className="fas fa-download"></i> Download</button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedApplication.screening_answers?.length > 0 && (
                <div className="ja-detail-section">
                  <div className="ja-detail-section-title"><i className="fas fa-question-circle"></i> Screening Answers</div>
                  {selectedApplication.screening_answers.map((qa, i) => (
                    <div key={i} className="ja-qa-item">
                      <p className="ja-question"><strong>Q{i + 1}:</strong> {qa.question}</p>
                      <p className="ja-answer">{qa.answer}</p>
                    </div>
                  ))}
                </div>
              )}

              {selectedApplication.rejection_reason && (
                <div className="ja-detail-section ja-section-danger">
                  <div className="ja-detail-section-title"><i className="fas fa-times-circle"></i> Rejection Reason</div>
                  <p style={{ color: 'var(--text-primary)', margin: 0, lineHeight: 1.7 }}>{selectedApplication.rejection_reason}</p>
                </div>
              )}
              {selectedApplication.interview_notes && (
                <div className="ja-detail-section ja-section-success">
                  <div className="ja-detail-section-title"><i className="fas fa-sticky-note"></i> Interview Notes</div>
                  <p style={{ color: 'var(--text-primary)', margin: 0, lineHeight: 1.7 }}>{selectedApplication.interview_notes}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedApplication(null); navigate(`/recruiter/seeker-profile/${selectedApplication.user_id}`); }}>
                <i className="fas fa-user"></i> View Profile
              </button>
              {!['interview','rejected','hired'].includes(selectedApplication.status) && (
                <>
                  <button className="btn ja-btn-interview" onClick={() => { setSelectedApplication(null); setInviteModal({ show: true, applicationId: selectedApplication.id }); }}>
                    <i className="fas fa-calendar-check"></i> Invite to Interview
                  </button>
                  <button className="btn btn-danger" onClick={() => { setSelectedApplication(null); setDeclineModal({ show: true, applicationId: selectedApplication.id }); setDeclineReason(''); }}>
                    <i className="fas fa-times"></i> Decline
                  </button>
                </>
              )}
              {selectedApplication.status === 'interview' && selectedApplication.interview_response === 'accepted' && (
                <button className="btn ja-btn-hire" onClick={() => { setSelectedApplication(null); handleHire(selectedApplication.id); }} disabled={processing}>
                  <i className="fas fa-user-tie"></i> Hire Candidate
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {emailModal.show && (
        <div className="modal-overlay" onClick={() => setEmailModal({ show: false, app: null })}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ padding: 0, border: 'none', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                <i className="fas fa-envelope" style={{ color: 'var(--primary)' }}></i> Email {emailModal.app?.applicant_name}
              </h2>
              <button className="modal-close" onClick={() => setEmailModal({ show: false, app: null })}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                <i className="fas fa-info-circle"></i> This will open your email client to send directly to <strong>{emailModal.app?.applicant_email}</strong>
              </p>
              {emailTemplates.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Load from Template</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <select className="form-control" defaultValue=""
                      onChange={e => e.target.value && applyTemplate(e.target.value, 'email')}>
                      <option value="">— Select a template —</option>
                      {emailTemplates.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-hint">Selecting a template will fill in the subject and message below.</div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">To</label>
                <input type="text" className="form-control" value={emailModal.app?.applicant_email || ''} readOnly
                  style={{ background: 'var(--surface-2)', cursor: 'not-allowed', opacity: 0.8 }} />
              </div>
              <div className="form-group">
                <label className="form-label">Subject</label>
                <input type="text" className="form-control" value={emailData.subject}
                  onChange={e => setEmailData({ ...emailData, subject: e.target.value })} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Message</label>
                <textarea className="form-control" rows="9" value={emailData.body}
                  onChange={e => setEmailData({ ...emailData, body: e.target.value })}
                  style={{ fontFamily: 'inherit', lineHeight: 1.7 }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEmailModal({ show: false, app: null })}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSendEmail}>
                <i className="fas fa-paper-plane"></i> Open Email Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interview Invite Modal */}
      {inviteModal.show && (
        <div className="modal-overlay" onClick={() => setInviteModal({ show: false, applicationId: null })}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ padding: 0, border: 'none', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                <i className="fas fa-calendar-check" style={{ color: 'var(--success)' }}></i> Send Interview Invitation
              </h2>
              <button className="modal-close" onClick={() => setInviteModal({ show: false, applicationId: null })}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              {emailTemplates.filter(t => t.category === 'interview').length > 0 && (
                <div className="form-group">
                  <label className="form-label">Load from Template</label>
                  <select className="form-control" defaultValue=""
                    onChange={e => {
                      if (!e.target.value) return;
                      const t = emailTemplates.find(t => t.id === parseInt(e.target.value));
                      if (t) setInviteData(d => ({ ...d, notes: t.body
                        .replace(/\{\{applicant_name\}\}/g, applications.find(a => a.id === inviteModal.applicationId)?.applicant_name || '')
                        .replace(/\{\{job_title\}\}/g, job?.title || '')
                        .replace(/\{\{company_name\}\}/g, job?.company || '')
                        .replace(/\{\{interview_date\}\}/g, d.date || '')
                        .replace(/\{\{interview_time\}\}/g, d.time || '')
                        .replace(/\{\{interview_location\}\}/g, d.location || '')
                      }));
                    }}>
                    <option value="">— Select an interview template —</option>
                    {emailTemplates.filter(t => t.category === 'interview').map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <div className="form-hint">Fills the notes field below with the template body.</div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input type="date" className="form-control" value={inviteData.date} onChange={e => setInviteData({ ...inviteData, date: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Time *</label>
                <input type="time" className="form-control" value={inviteData.time} onChange={e => setInviteData({ ...inviteData, time: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Location / Meeting Link *</label>
                <input type="text" className="form-control" placeholder="e.g. 123 Main St, Office 4B or https://meet.google.com/..." value={inviteData.location} onChange={e => setInviteData({ ...inviteData, location: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Notes (Optional)</label>
                <textarea className="form-control" rows="4" placeholder="Any preparation tips or additional info for the candidate..." value={inviteData.notes} onChange={e => setInviteData({ ...inviteData, notes: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setInviteModal({ show: false, applicationId: null })} disabled={processing}>Cancel</button>
              <button className="btn ja-btn-interview" onClick={handleSendInvite}
                disabled={processing || !inviteData.date || !inviteData.time || !inviteData.location.trim()}>
                {processing ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decline Modal */}
      {declineModal.show && (
        <div className="modal-overlay" onClick={() => setDeclineModal({ show: false, applicationId: null })}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ padding: 0, border: 'none', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                <i className="fas fa-times-circle" style={{ color: 'var(--danger)' }}></i> Decline Application
              </h2>
              <button className="modal-close" onClick={() => setDeclineModal({ show: false, applicationId: null })}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Reason for Declining *</label>
                <textarea className="form-control" rows="5" value={declineReason}
                  onChange={e => setDeclineReason(e.target.value)}
                  placeholder="Provide a reason to help the applicant understand your decision..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeclineModal({ show: false, applicationId: null })} disabled={processing}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDecline} disabled={processing || !declineReason.trim()}>
                {processing ? 'Processing...' : 'Confirm Decline'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Bulk Action Modal */}
      {bulkModal && (
        <div className="modal-overlay" onClick={() => setBulkModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ padding: 0, border: 'none', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                <i className="fas fa-bolt" style={{ color: 'var(--primary)' }}></i> Bulk Action — {selected.size} applicant{selected.size !== 1 ? 's' : ''}
              </h2>
              <button className="modal-close" onClick={() => setBulkModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Action</label>
                <select className="form-control" value={bulkAction} onChange={e => setBulkAction(e.target.value)}>
                  <option value="viewed">Mark as Viewed</option>
                  <option value="shortlist">Shortlist (clear below-threshold flag)</option>
                  <option value="reject">Reject & Send Email</option>
                </select>
              </div>
              {bulkAction === 'reject' && (
                <>
                  {emailTemplates.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">Rejection Template</label>
                      <select className="form-control" value={bulkTemplateId} onChange={e => setBulkTemplateId(e.target.value)}>
                        <option value="">Use default message</option>
                        {emailTemplates.filter(t => t.category === 'rejection').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Custom Rejection Message (optional)</label>
                    <textarea className="form-control" rows="4" value={bulkReason} onChange={e => setBulkReason(e.target.value)}
                      placeholder="Leave blank to use template or default message..." />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setBulkModal(false)} disabled={processing}>Cancel</button>
              <button className={`btn ${bulkAction === 'reject' ? 'btn-danger' : 'btn-primary'}`} onClick={handleBulkAction} disabled={processing}>
                <i className={`fas ${processing ? 'fa-spinner fa-spin' : 'fa-bolt'}`}></i>
                {processing ? 'Processing...' : `Apply to ${selected.size} applicant${selected.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject & Notify Modal */}
      {rejectNotifyModal.show && (
        <div className="modal-overlay" onClick={() => setRejectNotifyModal({ show: false, app: null })}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ padding: 0, border: 'none', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                <i className="fas fa-times-circle" style={{ color: 'var(--danger)' }}></i> Reject & Notify
              </h2>
              <button className="modal-close" onClick={() => setRejectNotifyModal({ show: false, app: null })}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              <div style={{ padding: '0.75rem 1rem', borderRadius: 8, background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <i className="fas fa-exclamation-triangle" style={{ color: '#ef4444', marginRight: 6 }}></i>
                <strong>{rejectNotifyModal.app?.applicant_name}</strong> scored below the screening threshold ({Math.round(rejectNotifyModal.app?.match_score || 0)}%). A rejection email will be sent automatically.
              </div>
              {emailTemplates.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Rejection Email Template</label>
                  <select className="form-control" value={rejectNotifyTemplateId} onChange={e => setRejectNotifyTemplateId(e.target.value)}>
                    <option value="">Use default message</option>
                    {emailTemplates.filter(t => t.category === 'rejection').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Custom Message (optional)</label>
                <textarea className="form-control" rows="4" value={rejectNotifyReason}
                  onChange={e => setRejectNotifyReason(e.target.value)}
                  placeholder="Leave blank to use the selected template or default message..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setRejectNotifyModal({ show: false, app: null })} disabled={processing}>Cancel</button>
              <button className="btn btn-danger" onClick={handleRejectAndNotify} disabled={processing}>
                <i className={`fas ${processing ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                {processing ? 'Sending...' : 'Reject & Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default JobApplications;
